import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuthEvents } from '@modules/Auth';
import { SCOPES, REDIRECT_URL } from '@/config';
import { useMsal } from '@azure/msal-react';

const FullPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const MainContent = styled.div`
  flex: 1;
  background-color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
`;

const ErrorHeading = styled.h1`
  font-size: 36px;
  font-weight: bold;
  color: #d32f2f;
  text-align: center;
  margin: 0 0 20px 0;
`;

const ErrorText = styled.p`
  font-size: 16px;
  color: black;
  text-align: center;
  margin: 10px 0;
  line-height: 1.5;
`;

const UserInfoText = styled.p`
  font-size: 16px;
  color: black;
  text-align: center;
  margin: 10px 0;
  
  strong {
    font-weight: bold;
  }
`;

const ContactText = styled.p`
  font-size: 16px;
  color: black;
  text-align: center;
  margin: 10px 0;
  
  a {
    color: blue;
    text-decoration: underline;
  }
`;

const SignOutButton = styled.button`
  background-color: #ffcc00;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  color: black;
  cursor: pointer;
  margin-top: 20px;
  
  &:hover {
    background-color: #ffd633;
  }
`;

const YellowFooter = styled.div`
  background-color: #ffcc00;
  padding: 10px;
  width: 100%;
`;

// TODO move this to a shared location e.g. fe-common (copied from AM)
interface JwtClaims {
  [key: string]: any;
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  preferred_username?: string;
  sub?: string;
}

/**
 * Decodes a JWT token and returns its claims.
 * JWT tokens are base64url encoded and have the format: header.payload.signature
 * This function extracts and decodes the payload.
 * TODO move this to a shared location e.g. fe-common (copied from AM)
 */
const decodeJwtToken = (token: string): JwtClaims | null => {
  try {
      const parts = token.split('.');
      if (parts.length !== 3) {
          return null;
      }
      
      // Decode base64url (replace - with +, _ with /, and add padding if needed)
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const decoded = atob(padded);
      
      return JSON.parse(decoded) as JwtClaims;
  } catch (error) {
      console.error('Failed to decode JWT token:', error);
      return null;
  }
};

/**
 * NotEnoughPrivileges component displays an authentication error page
 * when the user does not have sufficient privileges to access a resource.
 */
const NotEnoughPrivileges = () => {
  const { logout } = useAuthEvents();
  const {instance} = useMsal();
  const [jwtClaims, setJwtClaims] = useState<JwtClaims | null>(null);

  /**
   * Extracts user information from JWT token claims.
   */
  useEffect(() => {
      const extractTokenClaims = async () => {
          try {
              const account = instance.getActiveAccount();
              if (!account) {
                  return;
              }
              const redirectUri = `${window.location.origin}/blank.html`;
              const request = {
                  scopes: SCOPES,
                  account,
                  redirectUri,
              };
              const tokenResponse = await instance.acquireTokenSilent(request);
              if (tokenResponse?.accessToken) {
                  const claims = decodeJwtToken(tokenResponse.accessToken);
                  setJwtClaims(claims);
              }
          } catch (error) {
              // Token acquisition failed, will fall back to accountInfo
              console.error('Failed to acquire token:', error);
          }
      };

      extractTokenClaims();
  }, [instance]);

  return (
    <FullPageContainer>

      <MainContent>
        <ErrorHeading>Authentication Error</ErrorHeading>
        <ErrorText>
          You do not have sufficient privileges to access this resource. This usually indicates insufficient permissions or an expired session.
        </ErrorText>
        {jwtClaims?.name && (
          <UserInfoText>
            You are signed in as <strong>{jwtClaims.name}</strong>.
          </UserInfoText>
        )}
        <ContactText>
          If this problem persists, please contact the M³ Team at{' '}
          <a href="mailto:mcube.gho@dhl.com">mcube.gho@dhl.com</a> for assistance.
        </ContactText>
        <SignOutButton onClick={logout}>SIGN OUT NOW</SignOutButton>
      </MainContent>
      <YellowFooter />
    </FullPageContainer>
  );
};

export default NotEnoughPrivileges;
